import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { TrendingUp, Award, Target, DollarSign, Package, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI } from '../../lib/api';
import { formatPrice } from '../../lib/utils';

export default function SalesPerformancePage() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.listStaff();
      const items = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.items)
          ? response.data.items
          : [];
      setStaff(items);
    } catch (error) {
      console.error('Error loading staff:', error);
      toast.error('Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const advisors = staff.filter(s => s.role === 'ADVISOR');
  const sortedAdvisors = [...advisors].sort((a, b) => (b.total_sales || 0) - (a.total_sales || 0));
  
  const totalSales = advisors.reduce((sum, a) => sum + (a.total_sales || 0), 0);
  const totalOrders = advisors.reduce((sum, a) => sum + (a.orders_count || 0), 0);
  const averageOrderValue = totalOrders > 0 ? totalSales / totalOrders : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Performance des ventes</h1>
        <p className="text-muted-foreground">Suivi des ventes par conseiller</p>
      </div>

      {/* Global Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ventes totales</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPrice(totalSales)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Commandes</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalOrders}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Panier moyen</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPrice(averageOrderValue)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conseillers actifs</CardTitle>
            <UserCog className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{advisors.filter(a => (a.orders_count || 0) > 0).length}</div>
            <p className="text-xs text-muted-foreground">sur {advisors.length} total</p>
          </CardContent>
        </Card>
      </div>

      {/* Leaderboard */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-yellow-500" />
            Classement des conseillers
          </CardTitle>
          <CardDescription>Classement par chiffre d'affaires généré</CardDescription>
        </CardHeader>
        <CardContent>
          {sortedAdvisors.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Aucun conseiller n'a encore de ventes
            </div>
          ) : (
            <div className="space-y-4">
              {sortedAdvisors.map((advisor, index) => {
                const salesAmount = advisor.total_sales || 0;
                const ordersCount = advisor.orders_count || 0;
                const avgOrder = ordersCount > 0 ? salesAmount / ordersCount : 0;
                const salesPercentage = totalSales > 0 ? (salesAmount / totalSales) * 100 : 0;

                return (
                  <div
                    key={advisor.id}
                    className={`flex items-center gap-4 p-4 rounded-lg border ${
                      index === 0 ? 'bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800' :
                      index === 1 ? 'bg-gray-50 dark:bg-gray-950/20 border-gray-200 dark:border-gray-800' :
                      index === 2 ? 'bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800' :
                      ''
                    }`}
                  >
                    {/* Rank */}
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 flex-shrink-0">
                      {index === 0 ? (
                        <Award className="h-6 w-6 text-yellow-500" />
                      ) : index === 1 ? (
                        <Award className="h-6 w-6 text-gray-400" />
                      ) : index === 2 ? (
                        <Award className="h-6 w-6 text-orange-600" />
                      ) : (
                        <span className="text-xl font-bold text-muted-foreground">#{index + 1}</span>
                      )}
                    </div>

                    {/* Advisor Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-lg">
                          {advisor.name || `${advisor.first_name || ''} ${advisor.last_name || ''}`.trim()}
                        </p>
                        {index === 0 && <Badge className="bg-yellow-500 hover:bg-yellow-600">🏆 Top vendeur</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground">{advisor.email}</p>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-6 text-right">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Chiffre d'affaires</p>
                        <p className="text-lg font-bold text-primary">{formatPrice(salesAmount)}</p>
                        <p className="text-xs text-muted-foreground">
                          {salesPercentage.toFixed(1)}% du total
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Commandes</p>
                        <p className="text-lg font-bold">{ordersCount}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Panier moyen</p>
                        <p className="text-lg font-bold">{formatPrice(avgOrder)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle>Tableau détaillé</CardTitle>
          <CardDescription>Vue d'ensemble des performances individuelles</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-medium">Conseiller</th>
                  <th className="text-right p-3 font-medium">Commandes</th>
                  <th className="text-right p-3 font-medium">CA Total</th>
                  <th className="text-right p-3 font-medium">Panier Moyen</th>
                  <th className="text-right p-3 font-medium">% du Total</th>
                </tr>
              </thead>
              <tbody>
                {sortedAdvisors.map((advisor) => {
                  const salesAmount = advisor.total_sales || 0;
                  const ordersCount = advisor.orders_count || 0;
                  const avgOrder = ordersCount > 0 ? salesAmount / ordersCount : 0;
                  const salesPercentage = totalSales > 0 ? (salesAmount / totalSales) * 100 : 0;

                  return (
                    <tr key={advisor.id} className="border-b hover:bg-muted/50">
                      <td className="p-3">
                        <div>
                          <p className="font-medium">
                            {advisor.name || `${advisor.first_name || ''} ${advisor.last_name || ''}`.trim()}
                          </p>
                          <p className="text-sm text-muted-foreground">{advisor.email}</p>
                        </div>
                      </td>
                      <td className="p-3 text-right font-mono">{ordersCount}</td>
                      <td className="p-3 text-right font-mono font-semibold">{formatPrice(salesAmount)}</td>
                      <td className="p-3 text-right font-mono">{formatPrice(avgOrder)}</td>
                      <td className="p-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-20 h-2 bg-secondary rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary"
                              style={{ width: `${Math.min(salesPercentage, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12">{salesPercentage.toFixed(1)}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
